import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@deepguide-ai/ui/combobox'
import { MemberAvatar } from '@/components/compliance/MemberAvatar'
import { cn } from '@/lib/utils'

export interface UserSelectMember {
  user: {
    id: string
    name: string | null
    email: string
    image: string | null
    role: string | null
  }
  /**
   * Muted qualifier shown after the name, e.g. "self-review". Kept off
   * `user.name` because UserAvatar derives its initials and gradient from that
   * field — folding a suffix in would give the same person a different avatar.
   */
  hint?: string
}

interface UserSelectProps {
  id?: string
  allowUnassigned?: boolean
  value: string
  onValueChange: (value: string) => void
  members: UserSelectMember[]
  placeholder?: string
  className?: string
  disabled?: boolean
  isAdminMembers?: boolean
  ariaRequired?: boolean
}

export function UserSelect({
  id,
  allowUnassigned = false,
  value,
  onValueChange,
  members,
  placeholder = 'Select user...',
  className,
  disabled,
  isAdminMembers=false,
  ariaRequired,
}: UserSelectProps) {
  // Transform members to combobox data format
  const data = [
    ...(allowUnassigned ? [{ label: 'Unassigned', value: '_unassigned' }] : []),
    ...members.map((member) => ({
      label: member.user.name || member.user.email,
      value: member.user.id,
    })),
  ]

  const selectedMember = members.find((m) => m.user.id === value)
  const filteredMembers = isAdminMembers ? members.filter((m) => m.user?.role === 'ADMIN' || m.user?.role === 'CONTRIBUTOR') : members


  return (
    <Combobox
      data={data}
      type="user"
      value={value || (allowUnassigned ? '_unassigned' : '')}
      onValueChange={(next) => onValueChange(allowUnassigned && next === '_unassigned' ? '' : next)}
    >
      <ComboboxTrigger
        id={id}
        className={cn('w-full justify-between', className)}
        disabled={disabled}
        aria-required={ariaRequired}
      >
        {selectedMember ? (
          <div className="flex min-w-0 items-center gap-2">
            <MemberAvatar person={selectedMember.user} size="sm" />
            <span className="truncate">
              {selectedMember.user.name || selectedMember.user.email}
            </span>
            {selectedMember.hint && (
              <span className="shrink-0 text-xs text-muted-foreground">
                ({selectedMember.hint})
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </ComboboxTrigger>
      <ComboboxContent>
        <ComboboxInput placeholder="Search users..." />
        <ComboboxList>
          <ComboboxEmpty>No users found.</ComboboxEmpty>
          <ComboboxGroup>
            {allowUnassigned && <ComboboxItem value='_unassigned'>Unassigned</ComboboxItem>}
            {filteredMembers.map((member) => (
              <ComboboxItem
                key={member.user.id}
                value={member.user.id}
                className="flex items-center gap-2"
              >
                <MemberAvatar person={member.user} size="sm" />
                <div className="flex min-w-0 flex-col">
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate">
                      {member.user.name || member.user.email}
                    </span>
                    {member.hint && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        ({member.hint})
                      </span>
                    )}
                  </span>
                  {member.user.name && (
                    <span className="text-xs text-muted-foreground truncate">
                      {member.user.email}
                    </span>
                  )}
                </div>
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
